import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import portalApi from '../../../../lib/portalApi';
import {
  Bell,
  X,
  AlertCircle,
  UploadCloud,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';

export default function StartupOverviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showFixModal, setShowFixModal] = useState(false);
  const [fixedFile, setFixedFile] = useState(null);
  const [fixSuccess, setFixSuccess] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await portalApi.getStartupPortalOverview();
      setData(res);
    } catch (err) {
      console.error('Failed to load startup overview:', err);
      // Fallback gracefully so UI mockup elements always display
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const startup = data?.startup || {};

  const handleFixSubmit = (e) => {
    e.preventDefault();
    setFixSuccess(true);
    setTimeout(() => {
      setShowFixModal(false);
      setFixSuccess(false);
    }, 1500);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* ─── Top Header: Overview Title + Stage Pill + Bell + Avatar ─── */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] tracking-tight">
          Overview
        </h1>
        <div className="flex items-center gap-3">
          <div className="px-3.5 py-1 rounded-full border border-[#D42B92] bg-[#D42B92]/10 text-[#FF8EC5] text-xs font-semibold tracking-wide">
            Stage: Pilot
          </div>

          <div className="relative">
            <button
              onClick={() => navigate('/startup-dashboard/tasks')}
              className="relative p-2 rounded-xl text-[#8E8E99] hover:text-white transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#D42B92] text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                3
              </span>
            </button>
          </div>

          <div
            className="w-8 h-8 rounded-full bg-[#D42B92] text-white font-bold text-xs flex items-center justify-center shadow-md shadow-[#D42B92]/30 cursor-pointer"
            title={user?.fullName || user?.name || 'Founder'}
            onClick={() => navigate('/startup-dashboard/profile')}
          >
            {(user?.fullName || user?.name || 'A').charAt(0).toUpperCase()}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* ─── Top Grid: Left Stack (Cards 1, 2, 3) + Right (Card 4: Important updates) ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        {/* Left Column: 8 cols */}
        <div className="lg:col-span-8 flex flex-col gap-5">
          {/* ─── 1. Next step card ─── */}
          <div className="bg-[#131318] border border-[#2C2C34] hover:border-[#D42B92]/40 transition-colors rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#D42B92]">
                YOUR NEXT STEP
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-[#F2F2F4] tracking-tight">
                Resubmit GST registration
              </h2>
              <p className="text-xs sm:text-sm text-[#8E8E99]">
                Reviewer note: certificate is unreadable.
              </p>
            </div>
            <div className="shrink-0">
              <button
                onClick={() => setShowFixModal(true)}
                className="px-6 py-2.5 rounded-full bg-[#D42B92] hover:bg-[#b02279] text-white text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-[#D42B92]/20 hover:scale-[1.02] active:scale-[0.98]"
              >
                Fix now
              </button>
            </div>
          </div>

          {/* ─── 2. Summary cards (Single card with 4 column metrics) ─── */}
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-[#2C2C34]/60">
              {/* Metric 1: Program stage */}
              <div
                onClick={() => navigate('/startup-dashboard/tasks')}
                className="cursor-pointer group sm:px-3 first:pl-0"
              >
                <div className="text-xs font-medium text-[#8E8E99]">Program stage</div>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] my-1 group-hover:text-[#D42B92] transition-colors">
                  Pilot
                </div>
                <div className="text-xs text-[#8E8E99]">3 of 5 stages</div>
              </div>

              {/* Metric 2: Pending tasks */}
              <div
                onClick={() => navigate('/startup-dashboard/tasks')}
                className="cursor-pointer group pt-3 sm:pt-0 sm:px-4"
              >
                <div className="text-xs font-medium text-[#8E8E99]">Pending tasks</div>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] my-1 group-hover:text-[#D42B92] transition-colors">
                  5
                </div>
                <div className="text-xs text-red-400 font-medium">2 overdue</div>
              </div>

              {/* Metric 3: Meetings (week) */}
              <div
                onClick={() => navigate('/startup-dashboard/calendar')}
                className="cursor-pointer group pt-3 sm:pt-0 sm:px-4"
              >
                <div className="text-xs font-medium text-[#8E8E99]">Meetings (week)</div>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] my-1 group-hover:text-[#D42B92] transition-colors">
                  2
                </div>
                <div className="text-xs text-[#8E8E99]">Next: Thu 3 PM</div>
              </div>

              {/* Metric 4: Documents */}
              <div
                onClick={() => navigate('/startup-dashboard/vault')}
                className="cursor-pointer group pt-3 sm:pt-0 sm:px-4"
              >
                <div className="text-xs font-medium text-[#8E8E99]">Documents</div>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] my-1 group-hover:text-[#D42B92] transition-colors">
                  6 / 10
                </div>
                <div className="text-xs text-red-400 font-medium">1 rejected</div>
              </div>
            </div>
          </div>

          {/* ─── 3. Journey strip (Stepper timeline) ─── */}
          <div
            onClick={() => navigate('/startup-dashboard/tasks')}
            className="bg-[#131318] border border-[#2C2C34] hover:border-[#D42B92]/30 transition-colors rounded-2xl p-5 sm:p-6 cursor-pointer group"
          >
            <div className="text-sm sm:text-base font-bold text-[#F2F2F4] mb-6 flex items-center justify-between">
              <span>Program journey · 60% complete</span>
              <span className="text-xs text-[#8E8E99] font-normal group-hover:text-[#D42B92] transition-colors hidden sm:inline">
                Click to open Roadmap →
              </span>
            </div>

            {/* Stepper with connecting line */}
            <div className="relative flex items-center justify-between px-2 sm:px-6">
              {/* Connecting background line */}
              <div className="absolute left-6 right-6 top-3 h-0.5 bg-[#2C2C34] z-0" />
              {/* Progress active line up to Pilot (Stage 3 of 5 = 50%) */}
              <div className="absolute left-6 w-[50%] top-3 h-0.5 bg-gradient-to-r from-emerald-400 via-emerald-400 to-[#D42B92] z-0" />

              {/* Step 1: Idea (Done) */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-6 h-6 rounded-full bg-emerald-400 flex items-center justify-center text-black text-[10px] font-bold shadow-sm shadow-emerald-400/40">
                  ✓
                </div>
                <span className="text-xs font-medium text-emerald-400 mt-2">Idea</span>
              </div>

              {/* Step 2: MVP (Done) */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-6 h-6 rounded-full bg-emerald-400 flex items-center justify-center text-black text-[10px] font-bold shadow-sm shadow-emerald-400/40">
                  ✓
                </div>
                <span className="text-xs font-medium text-emerald-400 mt-2">MVP</span>
              </div>

              {/* Step 3: Pilot (Current Active) */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-6 h-6 rounded-full bg-[#D42B92] ring-4 ring-[#D42B92]/25 flex items-center justify-center text-white text-[10px] font-bold shadow-md shadow-[#D42B92]/50 animate-pulse">
                  ●
                </div>
                <span className="text-xs font-bold text-[#FF8EC5] mt-2 tracking-wide">Pilot</span>
              </div>

              {/* Step 4: GTM (Future) */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-6 h-6 rounded-full bg-[#2C2C34] flex items-center justify-center text-[#8E8E99] text-[10px] font-bold">
                  ●
                </div>
                <span className="text-xs font-medium text-[#8E8E99] mt-2">GTM</span>
              </div>

              {/* Step 5: Scale (Future) */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-6 h-6 rounded-full bg-[#2C2C34] flex items-center justify-center text-[#8E8E99] text-[10px] font-bold">
                  ●
                </div>
                <span className="text-xs font-medium text-[#8E8E99] mt-2">Scale</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: 4 cols — Card 4: Important updates */}
        <div className="lg:col-span-4 flex flex-col">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 flex-1 flex flex-col justify-between">
            <div>
              <div className="text-base font-bold text-[#F2F2F4] mb-4">
                Important updates
              </div>

              <div className="space-y-4">
                {/* Update 1 */}
                <div
                  onClick={() => setShowFixModal(true)}
                  className="group cursor-pointer p-2 rounded-xl hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
                    <div className="text-sm font-semibold text-red-400 group-hover:underline">
                      GST doc rejected
                    </div>
                  </div>
                  <div className="text-xs text-[#8E8E99] pl-4 mt-0.5">Today</div>
                </div>

                {/* Update 2 */}
                <div
                  onClick={() => navigate('/startup-dashboard/tasks')}
                  className="group cursor-pointer p-2 rounded-xl hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#D42B92] shrink-0" />
                    <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-[#D42B92] transition-colors">
                      New task from Riya
                    </div>
                  </div>
                  <div className="text-xs text-[#8E8E99] pl-4 mt-0.5">Yesterday</div>
                </div>

                {/* Update 3 */}
                <div
                  onClick={() => navigate('/startup-dashboard/calendar')}
                  className="group cursor-pointer p-2 rounded-xl hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-400 shrink-0" />
                    <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-sky-400 transition-colors">
                      Demo Day dates
                    </div>
                  </div>
                  <div className="text-xs text-[#8E8E99] pl-4 mt-0.5">27 Oct</div>
                </div>

                {/* Update 4 */}
                <div
                  onClick={() => navigate('/startup-dashboard/vault')}
                  className="group cursor-pointer p-2 rounded-xl hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-emerald-400 transition-colors">
                      Incorporation papers approved
                    </div>
                  </div>
                  <div className="text-xs text-[#8E8E99] pl-4 mt-0.5">22 Oct</div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#2C2C34]/40 mt-4">
              <span className="text-[11px] text-[#8E8E99]">
                Showing latest announcements & activity.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bottom Grid: 3 Equal Columns (Cards 5, 6, 7) ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* ─── 5. Upcoming ─── */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="text-base font-bold text-[#F2F2F4]">Upcoming</div>

          <div className="space-y-3.5">
            {/* Row 1 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Pitch review</div>
                <div className="text-xs text-[#8E8E99]">Thu 30 Oct · 3 PM</div>
              </div>
              <button
                onClick={() => navigate('/startup-dashboard/calendar')}
                className="px-3.5 py-1 rounded-full border border-[#D42B92] text-[#D42B92] hover:bg-[#D42B92]/10 text-xs font-semibold transition-colors"
              >
                Join
              </button>
            </div>

            {/* Row 2 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Inc. papers</div>
                <div className="text-xs text-[#8E8E99]">Deadline · 29 Oct</div>
              </div>
              <span className="px-3 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                Today
              </span>
            </div>

            {/* Row 3 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Pitch deck</div>
                <div className="text-xs text-[#8E8E99]">Due Fri 30 Oct</div>
              </div>
              <span className="px-3 py-0.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-400 text-xs font-semibold">
                Task
              </span>
            </div>

            {/* Row 4 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Check-in</div>
                <div className="text-xs text-[#8E8E99]">Mon 2 Nov · 11 AM</div>
              </div>
              <button
                onClick={() => navigate('/startup-dashboard/calendar')}
                className="px-3.5 py-1 rounded-full border border-[#D42B92] text-[#D42B92] hover:bg-[#D42B92]/10 text-xs font-semibold transition-colors"
              >
                Join
              </button>
            </div>
          </div>
        </div>

        {/* ─── 6. Pending tasks ─── */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="text-base font-bold text-[#F2F2F4]">Pending tasks</div>

          <div className="space-y-3.5">
            {/* Row 1 */}
            <div
              onClick={() => navigate('/startup-dashboard/tasks')}
              className="flex items-center justify-between gap-2 cursor-pointer group"
            >
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-[#D42B92] transition-colors">
                  Pitch deck v3
                </div>
                <div className="text-xs text-[#8E8E99]">High · 30 Oct</div>
              </div>
              <span className="px-3 py-0.5 rounded-full bg-white/5 border border-[#2C2C34] text-[#8E8E99] text-xs font-semibold">
                To Do
              </span>
            </div>

            {/* Row 2 */}
            <div
              onClick={() => navigate('/startup-dashboard/tasks')}
              className="flex items-center justify-between gap-2 cursor-pointer group"
            >
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-sky-400 transition-colors">
                  Interviews
                </div>
                <div className="text-xs text-[#8E8E99]">Med · 03 Nov</div>
              </div>
              <span className="px-3 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-400 text-xs font-semibold">
                Doing
              </span>
            </div>

            {/* Row 3 */}
            <div
              onClick={() => navigate('/startup-dashboard/tasks')}
              className="flex items-center justify-between gap-2 cursor-pointer group"
            >
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-sky-400 transition-colors">
                  Pilot report
                </div>
                <div className="text-xs text-[#8E8E99]">High · 08 Nov</div>
              </div>
              <span className="px-3 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-400 text-xs font-semibold">
                Doing
              </span>
            </div>

            {/* Row 4 */}
            <div
              onClick={() => navigate('/startup-dashboard/tasks')}
              className="flex items-center justify-between gap-2 cursor-pointer group"
            >
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4] group-hover:text-amber-400 transition-colors">
                  Investor list
                </div>
                <div className="text-xs text-[#8E8E99]">Low · waiting</div>
              </div>
              <span className="px-3 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold">
                Hold
              </span>
            </div>
          </div>
        </div>

        {/* ─── 7. Document status ─── */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="text-base font-bold text-[#F2F2F4]">Document status</div>

          <div className="space-y-3.5">
            {/* Row 1 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Incorporation</div>
                <div className="text-xs text-[#8E8E99]">Approved · v2</div>
              </div>
              <span className="px-3.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                OK
              </span>
            </div>

            {/* Row 2 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">GST registration</div>
                <div className="text-xs text-red-400 font-medium">Rejected</div>
              </div>
              <button
                onClick={() => setShowFixModal(true)}
                className="px-3.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25 text-xs font-semibold transition-colors"
              >
                Fix
              </button>
            </div>

            {/* Row 3 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Pitch deck</div>
                <div className="text-xs text-[#8E8E99]">Under review · v3</div>
              </div>
              <span className="px-3.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold">
                Review
              </span>
            </div>

            {/* Row 4 */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#F2F2F4]">Founder agmt</div>
                <div className="text-xs text-[#8E8E99]">Not uploaded</div>
              </div>
              <button
                onClick={() => navigate('/startup-dashboard/vault')}
                className="px-3.5 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-400 hover:bg-blue-500/25 text-xs font-semibold transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Fix Modal (Interactive Rejection & Re-upload Dialog) ─── */}
      {showFixModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-3xl max-w-lg w-full p-6 sm:p-7 relative shadow-2xl animate-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowFixModal(false)}
              className="absolute top-5 right-5 p-2 rounded-xl text-[#8E8E99] hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 text-red-400 mb-4">
              <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">GST Registration Rejected</h3>
                <p className="text-xs text-[#8E8E99]">Document Action Required</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 mb-5 text-xs sm:text-sm text-red-200 space-y-1">
              <strong className="block text-red-400">Reviewer Note:</strong>
              <p>Certificate is unreadable. Please ensure the scan is high-resolution with all corners and GSTIN clearly legible.</p>
            </div>

            {fixSuccess ? (
              <div className="py-6 text-center text-emerald-400 space-y-2">
                <CheckCircle2 className="w-8 h-8 mx-auto" />
                <div className="text-sm font-bold">New GST Document Submitted for Review!</div>
              </div>
            ) : (
              <form onSubmit={handleFixSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#8E8E99] uppercase mb-2">
                    Upload Replacement (PDF / PNG / JPG)
                  </label>
                  <label className="border-2 border-dashed border-[#2C2C34] hover:border-[#D42B92]/50 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors bg-[#08080A]/60">
                    <UploadCloud className="w-8 h-8 text-[#D42B92] mb-2" />
                    <span className="text-xs text-[#F2F2F4] font-medium">
                      {fixedFile ? fixedFile.name : 'Click to select clear document'}
                    </span>
                    <span className="text-[11px] text-[#8E8E99] mt-1">Max file size: 10MB</span>
                    <input
                      type="file"
                      required
                      className="hidden"
                      onChange={(e) => setFixedFile(e.target.files[0])}
                    />
                  </label>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowFixModal(false)}
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-[#8E8E99] hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-lg shadow-[#D42B92]/20"
                  >
                    Submit Replacement
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
